import type { LucideIcon } from 'lucide-react';
import {
  Home,
  BarChart3,
  Handshake,
  FileUp,
  Globe,
  TrendingUp,
  Library,
  FileEdit,
  StickyNote,
  Bot,
  Info,
  Users,
  BookOpenCheck,
  FileText,
  HelpCircle,
  Database,
  Compass,
  Megaphone,
} from 'lucide-react';
import { DOCUMENT_STUDIO, DOC_STUDIO_ENABLED } from '@/config/features';

export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  matchPaths?: string[];
  children?: SidebarItem[];
}

export interface SidebarSection {
  id: string;
  label: string;
  items: SidebarItem[];
  collapsible?: boolean;
}

/**
 * Which shell wraps the routed content. Lives here (a non-component layout-config
 * module) rather than in App.tsx so the pure decision stays unit-testable without
 * dragging in the App import graph — and so App.tsx keeps exporting only components.
 */
export type LayoutKind = 'public' | 'authenticated' | 'owner';

/**
 * Decide which shell wraps the routed content (chrome-level half of the
 * `raise_owner` deny-by-default wall).
 *
 * - public-only routes (and signed-out users) always get `PublicLayout` — this
 *   preserves the signed-out `/raises/:slug/claim` anonymous flow and avoids a
 *   sidebar flashing during the logout race.
 * - a signed-in `raise_owner` gets the minimal `OwnerLayout` (no developer
 *   sidebar).
 * - every other signed-in user (developer/investor/admin, incl. a dual-role
 *   developer who also owns a raise) keeps the full `AuthenticatedLayout`.
 */
export function resolveLayoutKind(params: {
  isAuthenticated: boolean;
  isRaiseOwner: boolean;
  isPublicOnlyRoute: boolean;
}): LayoutKind {
  if (params.isAuthenticated && !params.isPublicOnlyRoute) {
    return params.isRaiseOwner ? 'owner' : 'authenticated';
  }
  return 'public';
}

export function getSidebarConfig(
  options: { isAdmin?: boolean; isRaiseOwner?: boolean; documentStudioEnabled?: boolean } = {},
): SidebarSection[] {
  // Defense-in-depth: the scoped `raise_owner` persona renders inside `OwnerLayout`
  // (which has NO `AppSidebar`), so this config should never reach the sidebar for
  // an owner. Should it ever be wired in, hard-return an empty nav so none of the
  // walled-off developer surfaces (Deal Flow, Studio, DMS, Intelligence, …) leak
  // into an owner's chrome.
  if (options.isRaiseOwner) {
    return [];
  }

  const documentStudioEnabled =
    options.documentStudioEnabled ?? DOC_STUDIO_ENABLED;

  const sections: SidebarSection[] = [
    {
      id: 'main',
      label: 'MAIN',
      items: [
        {
          id: 'home',
          label: 'Home',
          icon: Home,
          to: '/home',
        },
        {
          id: 'invest',
          label: 'Deal Flow',
          icon: BarChart3,
          to: '/projects',
          matchPaths: ['/projects'],
        },
        {
          // Public Raises directory (sub-project ③B). Signed-in investors reach
          // the same investor-facing directory + profiles the public chrome
          // exposes; the ③A API resolves their (free/entitled) tier server-side.
          // `matchPaths` keeps the item active across the profile/company routes.
          id: 'explore-raises',
          label: 'Explore Raises',
          icon: Compass,
          to: '/raises',
          matchPaths: ['/raises', '/companies'],
        },
        {
          id: 'co-invest',
          label: 'Co-Invest',
          icon: Handshake,
          to: '/co-invest',
        },
        {
          id: 'submit-vdr',
          label: 'Submit VDR',
          icon: FileUp,
          to: '/submit-project',
        },
        {
          id: 'platform',
          label: 'Platform',
          icon: Globe,
          to: '/intelligence/global-infrastructure',
          children: [
            {
              id: 'global-infra',
              label: 'Global Infrastructure',
              icon: Globe,
              to: '/intelligence/global-infrastructure',
            },
            {
              id: 'investor',
              label: 'Investor Intelligence',
              icon: TrendingUp,
              to: '/intelligence/investor',
            },
          ],
        },
      ],
    },
    {
      id: 'workspace',
      label: 'WORKSPACE',
      items: [
        {
          id: 'artie-assistant',
          label: 'Artie AI Assistant',
          icon: Bot,
          to: '/artie-assistant',
        },
        {
          id: 'dms',
          label: 'Document Hub',
          icon: Library,
          to: '/dms',
          matchPaths: ['/dms'],
        },
        ...(documentStudioEnabled
          ? [
              {
                id: 'studio',
                label: 'Document Studio',
                icon: FileEdit,
                to: DOCUMENT_STUDIO.route,
              },
            ]
          : []),
        {
          id: 'project-notes',
          label: 'Project Notes',
          icon: StickyNote,
          to: '/project-notes',
        },
      ],
    },
    {
      id: 'help',
      label: 'HELP',
      collapsible: true,
      items: [
        {
          id: 'about',
          label: 'About',
          icon: Info,
          to: '/about',
        },
        {
          id: 'team',
          label: 'Team',
          icon: Users,
          to: '/team',
        },
        {
          id: 'blog',
          label: 'Blog',
          icon: BookOpenCheck,
          to: '/blog',
        },
        {
          id: 'publications',
          label: 'Publications',
          icon: FileText,
          to: '/publications',
        },
        {
          id: 'case-studies',
          label: 'Case Studies',
          icon: FileText,
          to: '/case-studies',
        },
        {
          id: 'help-center',
          label: 'Help Center',
          icon: HelpCircle,
          to: '/help',
        },
        {
          id: 'docs',
          label: 'Docs',
          icon: FileText,
          to: '/docs',
        },
      ],
    },
  ];

  if (options.isAdmin) {
    sections.splice(2, 0, {
      id: 'admin',
      label: 'ADMIN',
      collapsible: true,
      items: [
        {
          id: 'investor-db',
          label: 'Investor Database',
          icon: Database,
          to: '/admin/investors',
          matchPaths: ['/admin/investors'],
        },
        {
          id: 'active-raises',
          label: 'Active Raises',
          icon: TrendingUp,
          to: '/admin/active-raises',
          matchPaths: ['/admin/active-raises'],
        },
        {
          id: 'campaigns',
          label: 'Campaigns',
          icon: Megaphone,
          to: '/admin/campaigns',
          matchPaths: ['/admin/campaigns'],
        },
      ],
    });
  }

  return sections;
}
